import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Copy } from "lucide-react";
import { CONTACT_STATUS_CLASS, SEND_STATUS_CLASS, copyToClipboard } from "@/lib/comms";

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2" data-testid="comms-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-8 ${c === 0 ? "flex-[3]" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardsSkeleton({ count = 4, className = "h-28" }: { count?: number; className?: string }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => <Skeleton key={i} className={className} />)}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint: string; action?: React.ReactNode }) {
  return (
    <div className="text-center py-10 space-y-2">
      <p className="font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{hint}</p>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  return (
    <div className="text-center py-10 space-y-1">
      <p className="font-medium text-destructive">Could not load this data</p>
      <p className="text-sm text-muted-foreground">{(error as Error)?.message || "Unknown error"}</p>
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
  testId,
}: {
  label: string;
  value: number | string;
  hint?: string;
  icon?: any;
  tone?: "default" | "danger" | "good";
  testId?: string;
}) {
  const valueClass =
    tone === "danger"
      ? "text-destructive"
      : tone === "good"
        ? "text-emerald-600 dark:text-emerald-400"
        : "";
  return (
    <Card data-testid={testId || `stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="pt-5 space-y-1.5">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
          {Icon ? <Icon className="w-3.5 h-3.5" /> : null} {label}
        </div>
        <p className={`text-2xl font-bold ${valueClass}`}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </p>
        {hint ? <p className="text-xs text-muted-foreground leading-snug">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground text-xs">n/a</span>;
  const className = SEND_STATUS_CLASS[status] || CONTACT_STATUS_CLASS[status] || "bg-muted text-muted-foreground";
  return <Badge className={`border-0 ${className}`}>{status}</Badge>;
}

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const { toast } = useToast();
  return (
    <Button
      size="sm"
      variant="outline"
      type="button"
      onClick={() => copyToClipboard(text, () => toast({ title: "Copied", description: text }))}
      title={`Copy ${label || text}`}
    >
      <Copy className="w-3.5 h-3.5" />
      <span className="sr-only">Copy {label || text}</span>
    </Button>
  );
}
